import type { TranslationKey } from "../state/i18n";
import type { GenericSchema } from "valibot";
import type { IconName } from "../components/icon/Icon";
import type { VoidComponent } from "solid-js";
import type IntegrationId from "./IntegrationId";
import type { FieldConfig, ResourceSchema, VariableConfig } from "../fields/types";

export const INTEGRATION_TYPES = [
  "delete",
  "mail",
  "excel",
  "sftp",
  "googlesheetupdate",
  "googlesheets",
  "mongodbinsert",
  "onedrive",
  "task",
  "webdav",
  "word",
];

export type IntegrationType = (typeof INTEGRATION_TYPES)[number];

type PreviewItemProps<T> = { id: T };
type EditItemProps<T> = {
  integration: Integration<T>;
  updateConfiguration: (property: keyof T, value: unknown) => void;
  getError: (property: keyof T) => string | undefined;
  propertyKeys: Record<keyof T, TranslationKey>;
};

export type IntegrationDefinition<T, DTO> = {
  type: IntegrationType;
  legacy: {
    namespace: string;
    key: string;
    version: number;
  };
  schema: GenericSchema<T | unknown, T>;
  resourceSchema?: ResourceSchema; // Use for asynchronous validation only
  icon: IconName;
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  defaultProperties: () => Partial<T>;
  limit?: number;
  verifiable?: boolean;
  propertyKeys: Record<keyof T, TranslationKey>;
  PreviewItem: VoidComponent<PreviewItemProps<IntegrationId>>;
  OverlayItem: VoidComponent<PreviewItemProps<IntegrationId>>;
  EditItem: VoidComponent<EditItemProps<T>>;
  fromDto: (configuration: DTO, fields: FieldConfig[], variables: VariableConfig[]) => Partial<T>;
  toDto: (configuration: T, fields: FieldConfig[], variables: VariableConfig[]) => DTO;
};

export type IncompleteIntegrationDefinition<T, DTO> = Omit<
  IntegrationDefinition<T, DTO>,
  "PreviewItem" | "OverlayItem"
>;

export type Integration<T = Record<string, unknown>> = {
  id: IntegrationId;
  type: IntegrationType;
  legacy: {
    namespace: string;
    key: string;
    version: number;
  };
  name: string;
  configuration: T;
};

export type Dto<S> = S extends IntegrationDefinition<unknown, infer B> ? B : never;
